/**
 * DeviceGroupModal - Group control and management modal
 * Uses the EXACT same template structure as individual device modals
 */

import React, { useState, useMemo, useCallback } from 'react';
import { 
  X, 
  Edit2,
  Unlink,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  ToggleRight,
  Thermometer,
  Wind
} from 'lucide-react';
import { 
  DeviceControlGroup, 
  updateDeviceControlGroup, 
  deleteDeviceControlGroup 
} from '../services/userPreferencesApi';

// Use DeviceIcon like individual device modals
import DeviceIcon from './deviceIcons';

// Import trait control components (same as individual device modals)
import { Style05_TextButton as OnOffTextButton } from './deviceModals/traits/OnOff/Style05_TextButton';
import { Style01_Slider as BrightnessSlider } from './deviceModals/traits/Brightness/Style01_Slider';
import { Style01_Slider as ColorTempSlider } from './deviceModals/traits/ColorSetting/Style01_Slider';
import { Style02_Pills as FanSpeedPills } from './deviceModals/traits/FanSpeed/Style02_Pills';

// Blinds icon
const BlindsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 19V3H4v16H2v2h20v-2h-2zM6 5h12v2H6V5zm0 4h12v2H6V9zm0 4h12v2H6v-2z"/>
  </svg>
);

interface DeviceStatus {
  deviceId: string;
  displayName?: string;
  isOn?: boolean;
  online?: boolean;
  brightness?: number;
  colorTemp?: number;
  temperature?: number;
  openPercent?: number;
  fanSpeed?: string;
  [key: string]: any;
}

interface DeviceGroupModalProps {
  isOpen: boolean;
  group: DeviceControlGroup | null;
  deviceStatuses: Record<string, DeviceStatus>;
  onClose: () => void;
  onToggleGroup: (group: DeviceControlGroup, newState: boolean) => void;
  onSetBrightness: (group: DeviceControlGroup, brightness: number) => void;
  onSetColorTemp?: (group: DeviceControlGroup, colorTemp: number) => void;
  onSetTemperature?: (group: DeviceControlGroup, temperature: number) => void;
  onSetOpenPercent?: (group: DeviceControlGroup, openPercent: number) => void;
  onSetFanSpeed?: (group: DeviceControlGroup, fanSpeed: string) => void;
  onGroupUpdated: () => void;
  onGroupDeleted: () => void;
}

// Get device type name
const getDeviceTypeName = (deviceType: string): string => {
  const names: Record<string, string> = {
    light: '燈光',
    hvac: '空調',
    ac: '冷氣',
    curtain: '窗簾',
    blind: '捲簾',
    switch: '開關',
    outlet: '插座',
    fan: '風扇'
  };
  return names[deviceType] || deviceType;
};

const DeviceGroupModal: React.FC<DeviceGroupModalProps> = ({
  isOpen,
  group,
  deviceStatuses,
  onClose,
  onToggleGroup,
  onSetBrightness,
  onSetColorTemp,
  onSetTemperature,
  onSetOpenPercent,
  onSetFanSpeed,
  onGroupUpdated,
  onGroupDeleted
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMemberList, setShowMemberList] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  // Aggregate status from all devices in the group
  const aggregatedStatus = useMemo(() => {
    if (!group) return null;
    
    const statuses = group.devices
      .map(sn => deviceStatuses[sn])
      .filter(Boolean);
    
    if (statuses.length === 0) {
      return {
        anyOn: false,
        allOnline: true,
        onlineCount: 0,
        offlineCount: group.devices.length,
        avgBrightness: 0,
        avgColorTemp: 4000,
        avgTemperature: 24,
        avgOpenPercent: 0,
        currentFanSpeed: 'auto'
      };
    }
    
    const onlineStatuses = statuses.filter(s => s.online !== false);
    const onStatuses = statuses.filter(s => s.isOn === true);
    
    const brightnessValues = statuses.map(s => s.brightness).filter(v => typeof v === 'number') as number[];
    const colorTempValues = statuses.map(s => s.colorTemp).filter(v => typeof v === 'number') as number[];
    const tempValues = statuses.map(s => s.temperature).filter(v => typeof v === 'number') as number[];
    const openPercentValues = statuses.map(s => s.openPercent).filter(v => typeof v === 'number') as number[];
    
    return {
      anyOn: onStatuses.length > 0,
      allOnline: onlineStatuses.length === statuses.length,
      onlineCount: onlineStatuses.length,
      offlineCount: statuses.length - onlineStatuses.length,
      avgBrightness: brightnessValues.length > 0 
        ? Math.round(brightnessValues.reduce((a, b) => a + b, 0) / brightnessValues.length) 
        : 50,
      avgColorTemp: colorTempValues.length > 0 
        ? Math.round(colorTempValues.reduce((a, b) => a + b, 0) / colorTempValues.length) 
        : 4000,
      avgTemperature: tempValues.length > 0 
        ? Math.round(tempValues.reduce((a, b) => a + b, 0) / tempValues.length) 
        : 24,
      avgOpenPercent: openPercentValues.length > 0 
        ? Math.round(openPercentValues.reduce((a, b) => a + b, 0) / openPercentValues.length) 
        : 0,
      // For fan speed, get the most common value or first available
      currentFanSpeed: (() => {
        const fanSpeedValues = statuses.map(s => s.fanSpeed).filter(Boolean) as string[];
        if (fanSpeedValues.length === 0) return 'auto';
        // Return the most common fan speed
        const counts: Record<string, number> = {};
        fanSpeedValues.forEach(v => { counts[v] = (counts[v] || 0) + 1; });
        return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      })()
    };
  }, [group, deviceStatuses]);

  const handleStartEdit = () => {
    if (group) {
      setEditName(group.name);
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditName('');
  };

  const handleSaveEdit = async () => {
    if (group && editName.trim()) {
      try {
        await updateDeviceControlGroup(group.id, { name: editName.trim() });
        onGroupUpdated();
        setIsEditing(false);
      } catch (error) {
        console.error('Failed to update group name:', error);
      }
    }
  };

  const handleDeleteGroup = async () => {
    if (!group) return;
    setIsDeleting(true);
    try {
      await deleteDeviceControlGroup(group.id);
      onGroupDeleted();
      onClose();
    } catch (error) {
      console.error('Failed to delete group:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Control handlers with loading state
  const handleToggle = async () => {
    if (group && aggregatedStatus) {
      setIsExecuting(true);
      try {
        await onToggleGroup(group, !aggregatedStatus.anyOn);
      } finally {
        setTimeout(() => setIsExecuting(false), 500);
      }
    }
  };

  const handleBrightnessChange = (value: number) => {
    if (group) {
      onSetBrightness(group, value);
    }
  };

  const handleColorTempChange = (value: number) => {
    if (group && onSetColorTemp) {
      onSetColorTemp(group, value);
    }
  };

  const handleTemperatureChange = (value: number) => {
    if (group && onSetTemperature) {
      onSetTemperature(group, value);
    }
  };

  const handleOpenPercentChange = (value: number) => {
    if (group && onSetOpenPercent) {
      onSetOpenPercent(group, value);
    }
  };

  const handleFanSpeedChange = (speed: string) => {
    if (group && onSetFanSpeed) {
      onSetFanSpeed(group, speed);
    }
  };

  // Get display name for device
  const getDisplayName = useCallback((deviceSN: string): string => {
    const status = deviceStatuses[deviceSN];
    return status?.displayName || deviceSN;
  }, [deviceStatuses]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(() => {
    if (isEditing) {
      setIsEditing(false);
      setEditName('');
    } else {
      onClose();
    }
  }, [isEditing, onClose]);

  if (!isOpen || !group || !aggregatedStatus) return null;

  // Map deviceType for icon
  const iconDeviceType = group.deviceType === 'ac' ? 'hvac' : group.deviceType;

  // Render the large visual icon based on device type (same as individual device modals)
  const renderVisualIcon = () => {
    const isOn = aggregatedStatus.anyOn;
    
    switch (group.deviceType) {
      case 'light':
        return (
          <div className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 ${
            isOn 
              ? 'bg-gradient-to-br from-yellow-300 to-amber-400 shadow-lg shadow-yellow-500/50' 
              : 'bg-slate-700'
          }`}>
            <Lightbulb className={`w-16 h-16 ${isOn ? 'text-white' : 'text-slate-500'}`} />
          </div>
        );
      case 'switch':
        return (
          <div className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 ${
            isOn 
              ? 'bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg shadow-blue-500/50' 
              : 'bg-slate-700'
          }`}>
            <ToggleRight className={`w-16 h-16 ${isOn ? 'text-white' : 'text-slate-500'}`} />
          </div>
        );
      case 'hvac':
      case 'ac':
        return (
          <div className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 ${
            isOn 
              ? 'bg-gradient-to-br from-cyan-400 to-blue-500 shadow-lg shadow-cyan-500/50' 
              : 'bg-slate-700'
          }`}>
            <Thermometer className={`w-16 h-16 ${isOn ? 'text-white' : 'text-slate-500'}`} />
          </div>
        );
      case 'fan':
        return (
          <div className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 ${
            isOn 
              ? 'bg-gradient-to-br from-green-400 to-emerald-500 shadow-lg shadow-green-500/50' 
              : 'bg-slate-700'
          }`}>
            <Wind className={`w-16 h-16 ${isOn ? 'text-white' : 'text-slate-500'}`} />
          </div>
        );
      case 'curtain':
      case 'blind':
        return (
          <div className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 ${
            isOn 
              ? 'bg-gradient-to-br from-purple-400 to-indigo-500 shadow-lg shadow-purple-500/50' 
              : 'bg-slate-700'
          }`}>
            <BlindsIcon className={`w-16 h-16 ${isOn ? 'text-white' : 'text-slate-500'}`} />
          </div>
        );
      default:
        return (
          <div className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 ${
            isOn ? 'bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg shadow-blue-500/50' : 'bg-slate-700'
          }`}>
            <DeviceIcon deviceType={iconDeviceType as any} isOn={isOn} size={64} />
          </div>
        );
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4" 
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] rounded-3xl w-full max-w-sm max-h-[90vh] overflow-hidden shadow-2xl border border-slate-800/50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - EXACTLY like device modal */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DeviceIcon 
              deviceType={iconDeviceType as any} 
              isOn={aggregatedStatus.anyOn} 
              size={20} 
              className="flex-shrink-0"
            />
            {isEditing ? (
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 min-w-0 px-2 py-1 text-sm font-bold border border-slate-600 rounded bg-slate-800/80 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveEdit();
                    }
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                />
                <button 
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSaveEdit}
                  className="p-1 text-green-500 hover:text-green-400 flex-shrink-0"
                  title="儲存"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button 
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleCancelEdit}
                  className="p-1 text-slate-400 hover:text-slate-300 flex-shrink-0"
                  title="取消"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-base font-bold text-white truncate">{group.name}</h2>
                <button 
                  onClick={handleStartEdit}
                  className="p-1 hover:bg-slate-700/50 rounded transition-colors flex-shrink-0"
                >
                  <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Online status badge - same as device modal */}
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              aggregatedStatus.allOnline 
                ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                : aggregatedStatus.onlineCount > 0
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
            }`}>
              {aggregatedStatus.allOnline 
                ? 'ONLINE' 
                : aggregatedStatus.onlineCount > 0 
                  ? `${aggregatedStatus.onlineCount}/${group.devices.length}` 
                  : 'OFFLINE'}
            </span>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="px-5 pb-6 space-y-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Delete Confirmation */}
          {showDeleteConfirm && (
            <div className="p-4 bg-red-900/30 border border-red-500/30 rounded-xl">
              <div className="flex items-center gap-2 text-red-400 mb-3">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">確定要解散此群組嗎？</span>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                群組內的設備將恢復為獨立控制，此操作無法復原。
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleDeleteGroup}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-colors disabled:opacity-50"
                >
                  {isDeleting ? '處理中...' : '確定解散'}
                </button>
              </div>
            </div>
          )}

          {/* Visual Icon - same as device modal */}
          <div className="flex justify-center">
            {renderVisualIcon()}
          </div>

          {/* Power Control - using OnOffTextButton same as device modal */}
          <OnOffTextButton
            isOn={aggregatedStatus.anyOn}
            onToggle={handleToggle}
            disabled={isExecuting}
            loading={isExecuting}
            label="電源"
          />

          {/* Light Controls - same as Light template */}
          {group.deviceType === 'light' && (
            <>
              <BrightnessSlider
                value={aggregatedStatus.avgBrightness}
                min={1}
                max={100}
                onChange={handleBrightnessChange}
                onChangeEnd={handleBrightnessChange}
                disabled={isExecuting || !aggregatedStatus.anyOn}
                loading={isExecuting}
                label="亮度"
              />

              {onSetColorTemp && (
                <ColorTempSlider
                  value={aggregatedStatus.avgColorTemp}
                  onChange={handleColorTempChange}
                  disabled={isExecuting || !aggregatedStatus.anyOn}
                  min={2700}
                  max={6500}
                  label="色溫"
                />
              )}
            </>
          )}

          {/* HVAC Controls */}
          {(group.deviceType === 'hvac' || group.deviceType === 'ac') && onSetTemperature && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-300 text-sm">目標溫度</span>
                <span className="text-cyan-400 text-lg font-semibold">{aggregatedStatus.avgTemperature}°C</span>
              </div>
              <input
                type="range"
                min={16}
                max={30}
                value={aggregatedStatus.avgTemperature}
                onChange={(e) => handleTemperatureChange(Number(e.target.value))}
                className="w-full h-2 bg-slate-700/50 rounded-full appearance-none cursor-pointer accent-cyan-500"
              />
              <div className="flex justify-between text-xs text-slate-500">
                <span>16°C</span>
                <span>30°C</span>
              </div>
            </div>
          )}

          {/* Curtain/Blind Controls */}
          {(group.deviceType === 'curtain' || group.deviceType === 'blind') && onSetOpenPercent && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-300 text-sm">開度</span>
                <span className="text-blue-400 text-lg font-semibold">{aggregatedStatus.avgOpenPercent}%</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={aggregatedStatus.avgOpenPercent}
                onChange={(e) => handleOpenPercentChange(Number(e.target.value))}
                className="w-full h-2 bg-slate-700/50 rounded-full appearance-none cursor-pointer accent-blue-500"
              />
              <div className="flex justify-between text-xs text-slate-500">
                <span>關閉</span>
                <span>全開</span>
              </div>
            </div>
          )}

          {/* Fan Controls */}
          {group.deviceType === 'fan' && onSetFanSpeed && (
            <div className={`transition-opacity ${!aggregatedStatus.anyOn ? 'opacity-40 pointer-events-none' : ''}`}>
              <FanSpeedPills
                value={aggregatedStatus.currentFanSpeed}
                options={['auto', 'low', 'medium', 'high']}
                disabled={isExecuting || !aggregatedStatus.anyOn}
                loading={isExecuting}
                onChange={handleFanSpeedChange}
                labels={{ 'auto': '自動', 'low': '低速', 'medium': '中速', 'high': '高速' }}
              />
            </div>
          )}
        </div>

        {/* Footer - same structure as device modal */}
        <div className="px-5 py-3 border-t border-slate-800/50 bg-slate-900/30 space-y-2">
          {/* Member List - collapsible */}
          <button
            onClick={() => setShowMemberList(!showMemberList)}
            className="w-full flex items-center justify-between py-1 px-2 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors"
          >
            <span className="text-xs text-slate-400">
              群組成員 ({group.devices.length} 個{getDeviceTypeName(group.deviceType)})
            </span>
            {showMemberList ? (
              <ChevronUp className="w-4 h-4 text-slate-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-500" />
            )}
          </button>
          
          {showMemberList && (
            <div className="max-h-32 overflow-y-auto space-y-1 py-1">
              {group.devices.map((sn) => {
                const status = deviceStatuses[sn];
                const isOnline = status?.online !== false;
                const isOn = status?.isOn;
                
                return (
                  <div 
                    key={sn}
                    className="flex items-center justify-between py-1.5 px-2 bg-slate-800/30 rounded"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-1.5 h-1.5 rounded-full ${isOnline ? (isOn ? 'bg-green-400' : 'bg-slate-500') : 'bg-red-400'}`} />
                      <span className="text-xs text-slate-300 truncate">{getDisplayName(sn)}</span>
                    </div>
                    <span className="text-[10px] text-slate-500">{isOnline ? (isOn ? '開' : '關') : '離線'}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Ungroup button */}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full flex items-center justify-center gap-2 py-2 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors text-sm"
          >
            <Unlink className="w-4 h-4" />
            <span>解散群組</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeviceGroupModal;
